import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  is_using_temporary_password: boolean;
  temporary_password_set_at: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  onProfileUpdate: (callback: () => void) => () => void;
  needsPasswordChange: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileUpdateCallbacks, setProfileUpdateCallbacks] = useState<(() => void)[]>([]);

  // Register a callback to be called when profile is updated
  const onProfileUpdate = useCallback((callback: () => void) => {
    setProfileUpdateCallbacks(prev => [...prev, callback]);
    
    // Return unsubscribe function
    return () => {
      setProfileUpdateCallbacks(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  // Notify all registered callbacks
  const notifyProfileUpdate = useCallback(() => {
    profileUpdateCallbacks.forEach(callback => callback());
  }, [profileUpdateCallbacks]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Set the token in the API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Try to get user profile
      api.get('/api/auth/profile')
        .then(response => {
          const body = response.data;
          setUser(body.user || body);
        })
        .catch(() => {
          // Token is invalid, remove both tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          delete api.defaults.headers.common['Authorization'];
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post('/api/auth/login', {
        username,
        password,
      });

      const { access_token, refresh_token, user: userData } = response.data;
      
      // Store token
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Set user
      setUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await api.put('/api/auth/profile', data);
      const body = response.data;
      
      // Update user state with the response data
      setUser(body.user || body);
      
      // Also refresh the profile from server to ensure consistency
      await refreshProfile();
      
      // Notify all components that profile has been updated
      notifyProfileUpdate();
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    try {
      const response = await api.get('/api/auth/profile');
      const body = response.data;
      setUser(body.user || body);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  // Check if user needs to change temporary password
  const needsPasswordChange = user?.is_using_temporary_password || false;

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateProfile,
    refreshProfile,
    onProfileUpdate,
    needsPasswordChange,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
